import React from "react";
import { ChevronDown, Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ImageReferenceUpload } from "./ImageReferenceUpload";

interface ImageSettings {
  model: string;
  aspect_ratio: string;
  num_outputs: number;
  output_format: string;
  source_images: string[];
}

interface ImageQuickSettingsProps {
  settings: ImageSettings;
  onSettingsChange: (settings: ImageSettings) => void;
  hasChangedSettings: boolean;
  onUseDefaults: () => void;
}

export function ImageQuickSettings({
  settings,
  onSettingsChange,
  hasChangedSettings,
  onUseDefaults,
}: ImageQuickSettingsProps) {
  const updateSetting = (key: keyof ImageSettings, value: any) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const modelOptions = [
    { value: "flux-schnell", label: "Flux Schnell", isFree: true, supportsImageInput: false, supportsMultiple: false },
    { value: "hidream-l1-fast", label: "HiDream L1 Fast", isFree: true, supportsImageInput: false, supportsMultiple: false },
    { value: "minimax-image-01", label: "MiniMax Image", isFree: false, supportsImageInput: true, supportsMultiple: true },
    { value: "flux-dev", label: "Black Forest Dev", isFree: false, supportsImageInput: false, supportsMultiple: true },
    { value: "nano-banana", label: "Nano Banana", isFree: false, supportsImageInput: true, supportsMultiple: false },
  ];

  const aspectOptions = [
    { value: "1:1", label: "1:1 (Square)" },
    { value: "16:9", label: "16:9 (Landscape)" },
    { value: "9:16", label: "9:16 (Portrait)" },
    { value: "4:3", label: "4:3" },
    { value: "3:4", label: "3:4" },
  ];

  const formatOptions = settings.model === "nano-banana" 
    ? [
        { value: "jpg", label: "JPG" },
        { value: "png", label: "PNG" },
      ]
    : [
        { value: "webp", label: "WebP" },
        { value: "png", label: "PNG" },
        { value: "jpg", label: "JPG" },
      ];

  const countOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
  ];

  const currentModel = modelOptions.find(m => m.value === settings.model);
  const showImageUpload = currentModel?.supportsImageInput;
  const showMultipleImages = currentModel?.supportsMultiple ?? false;
  const buttonClass = "rounded-full bg-secondary hover:bg-secondary/80 text-secondary-foreground px-2.5 md:px-4 py-1.5 md:py-2 h-8 md:h-9 text-xs md:text-sm font-medium";

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
      {/* Reference image upload for Nano Banana - shown before Model */}
      {showImageUpload && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Reference</span>
          <ImageReferenceUpload
            images={settings.source_images}
            onImagesChange={(images) => updateSetting("source_images", images)}
            maxImages={4}
          />
        </div>
      )}

      {/* Model */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Model</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {currentModel?.label || "Flux Schnell"} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {modelOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => {
                  // Reset format to jpg if switching to nano-banana with unsupported format
                  // Also clear source images if switching away from models that support it
                  // Force num_outputs to 1 for models that don't support multiple
                  const supportsImageInput = opt.supportsImageInput;
                  const supportsMultiple = opt.supportsMultiple;
                  let newSettings = { ...settings, model: opt.value };
                  
                  if (opt.value === "nano-banana" && settings.output_format === "webp") {
                    newSettings.output_format = "jpg";
                  }
                  if (!supportsImageInput && settings.source_images.length > 0) {
                    newSettings.source_images = [];
                  }
                  if (!supportsMultiple && settings.num_outputs > 1) {
                    newSettings.num_outputs = 1;
                  }
                  
                  onSettingsChange(newSettings);
                }}
                className="flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  {opt.label}
                  {opt.isFree && <span className="text-xs text-green-500 font-medium">(Free)</span>}
                </span>
                {settings.model === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Aspect Ratio */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Aspect Ratio</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {aspectOptions.find((a) => a.value === settings.aspect_ratio)?.label || settings.aspect_ratio} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {aspectOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => updateSetting("aspect_ratio", opt.value)}
                className="flex items-center justify-between"
              >
                {opt.label}
                {settings.aspect_ratio === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Format */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Format</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {settings.output_format.toUpperCase()} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {formatOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => updateSetting("output_format", opt.value)}
                className="flex items-center justify-between"
              >
                {opt.label}
                {settings.output_format === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Number of Images - Only for models that support multiple */}
      {showMultipleImages && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground">Images</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" className={buttonClass}>
                {settings.num_outputs}x <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border">
              {countOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => updateSetting("num_outputs", opt.value)}
                  className="flex items-center justify-between"
                >
                  {opt.label} image{opt.value > 1 ? "s" : ""}
                  {settings.num_outputs === opt.value && <Check className="h-4 w-4 ml-2" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Reset Button */}
      {hasChangedSettings && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs text-muted-foreground opacity-0">Reset</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onUseDefaults}
            className="rounded-full h-9 px-3 text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
